import { Box, CloseButton, Flex, IconButton, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/client/services/state/authStore';
import { useConfigStore } from '@/client/services/state/configStore';
import { AdminMessagingService } from '@/client/components/admin/messaging/api/AdminMessagingService';

interface AnnouncementItem {
  id: string;
  content: string;
  translations?: { languageCode: string; content: string }[];
}

interface AppAnnouncementBannerProps {
  languageCode?: string;
}

export default function AppAnnouncementBanner({ languageCode }: AppAnnouncementBannerProps) {
  const { i18n } = useTranslation();
  const { config } = useConfigStore();
  const { authConfig } = useAuthStore();
  const location = useLocation();
  const { pathname } = location;

  const isAuthenticated = !!authConfig?.user;

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);

  const { data: activeAnnouncements } = useQuery({
    queryKey: ['activeAppAnnouncements', pathname],
    queryFn: () => AdminMessagingService.getActiveAnnouncements(pathname),
    enabled: isAuthenticated,
  });

  const announcements: AnnouncementItem[] = isAuthenticated
    ? (activeAnnouncements ?? [])
    : (config.announcements ?? []).filter((a) => a.routes.includes(pathname));

  const visibleAnnouncements = announcements.filter((a) => !dismissedIds.has(a.id));

  const resolveContent = (announcement: AnnouncementItem) => {
    const lang = languageCode ?? i18n.language;
    const translation = announcement.translations?.find((tr) => tr.languageCode === lang);
    return translation?.content ?? announcement.content;
  };

  useEffect(() => {
    setCurrentIndex(0);
  }, [pathname]);

  const dismiss = (id: string) => {
    setDismissedIds((prev) => new Set([...prev, id]));
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  if (visibleAnnouncements.length === 0) return null;

  const current = visibleAnnouncements[currentIndex];
  const hasMultiple = visibleAnnouncements.length > 1;

  return (
    <Box
      position="fixed"
      top="20px"
      left="50%"
      transform="translateX(-50%)"
      width={{ base: '95%', lg: '50%' }}
      backgroundColor="#102d47"
      paddingY="20px"
      paddingX="40px"
      borderRadius="8px"
      boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
      zIndex={1000}
    >
      <Text
        color="white"
        textAlign="center"
        fontWeight={500}
        fontSize={{ base: '12px', md: '14px' }}
      >
        {resolveContent(current)}
      </Text>

      {hasMultiple && (
        <Flex justifyContent="center" gap={2} marginTop={2}>
          {visibleAnnouncements.map((announcement, idx) => (
            <Box
              key={announcement.id}
              width="6px"
              height="6px"
              borderRadius="full"
              backgroundColor={idx === currentIndex ? 'white' : 'whiteAlpha.400'}
              cursor="pointer"
              onClick={() => setCurrentIndex(idx)}
            />
          ))}
        </Flex>
      )}

      {hasMultiple && currentIndex > 0 && (
        <IconButton
          aria-label="Previous announcement"
          icon={<Text color="white" fontSize="18px">‹</Text>}
          variant="ghost"
          position="absolute"
          left="8px"
          top="50%"
          transform="translateY(-50%)"
          onClick={() => setCurrentIndex((prev) => prev - 1)}
          _hover={{ backgroundColor: 'whiteAlpha.200' }}
        />
      )}

      {hasMultiple && currentIndex < visibleAnnouncements.length - 1 && (
        <IconButton
          aria-label="Next announcement"
          icon={<Text color="white" fontSize="18px">›</Text>}
          variant="ghost"
          position="absolute"
          right="30px"
          top="50%"
          transform="translateY(-50%)"
          onClick={() => setCurrentIndex((prev) => prev + 1)}
          _hover={{ backgroundColor: 'whiteAlpha.200' }}
        />
      )}

      <CloseButton
        color="white"
        onClick={() => dismiss(current.id)}
        position="absolute"
        top="5px"
        right="5px"
      />
    </Box>
  );
}
